import type { Address } from './generic';

export interface PersonForm {
  memberInfo: {
    basicInfo: {
      code: number | null;
      first: string;
      middle: string;
      last: string;
      ssn: string;
      ssnType: number;
      birthDate: string;
      maritalStatus: number;
      identityType: number;
      identityNumber: string;
      suffix: string;
    };
    address: {
      street: string;
      extra: string;
      city: string;
      state: string;
      zip: string;
      status: number;
      lengthAt: number | null;
      lengthAtInMonths: number | null;
      lengthAtDuration: string;
      rentAmount: string;
      isCopied: boolean;
    };
    prevAddress: {
      street: string;
      status: number;
      lengthAt: number | null;
      lengthAtInMonths: number | null;
      lengthAtDuration: string;
    };
    contact: {
      email: string;
      homePhone: string;
      mobilePhone: string;
      workPhone: string;
    };
    disclosures: {
      usCitizen: number;
      bankruptcy: number;
      cosignerOnOtherLoan: number;
      cosignerForWhom: string;
      cosignerToWhom: string;
      otherCredit: boolean;
      otherCreditName: string;
      partyInLawsuit: number;
      pastForeclosure: number;
      pastRepossession: number;
      judgement: number;
      incomeDecline: number;
      obligations: number;
      pastDueBills: number;
      textMessageConsent: number;
    };
  };
  employment: {
    current: {
      employmentStatus: number;
      employer: string;
      startDate: string;
      addressFields: Address;
      occupation: string;
      supervisor: string;
      hoursPerWeek: number;
      workPhone: string;
    };
    previous: {
      employer: string;
      startDate: string;
      endDate: string;
      address: string;
      hoursPerWeek: number;
      occupation: string;
    };
  };
  income: {
    grossMonthly: string;
    grossPay: number;
    grossPayDuration: string;
    additionalIncome: AdditionalIncome[];
  };
}

export interface AdditionalIncome {
  type: number;
  otherDescription: string;
  amount: number;
  amountInMonths: number;
  duration: string;
  hoursPerWeek: number;
}

export const defaultPersonForm: PersonForm = {
  memberInfo: {
    basicInfo: {
      code: 0,
      first: '',
      middle: '',
      last: '',
      ssn: '',
      ssnType: 0,
      birthDate: '',
      maritalStatus: 0,
      identityType: 0,
      identityNumber: '',
      suffix: '',
    },
    address: {
      street: '',
      extra: '',
      city: '',
      state: '',
      zip: '',
      status: 1,
      lengthAt: null,
      lengthAtInMonths: null,
      lengthAtDuration: 'years',
      rentAmount: '',
      isCopied: false,
    },
    prevAddress: {
      street: '',
      status: 0,
      lengthAt: null,
      lengthAtInMonths: null,
      lengthAtDuration: 'years',
    },
    contact: {
      email: '',
      homePhone: '',
      mobilePhone: '',
      workPhone: '',
    },
    disclosures: {
      usCitizen: 0,
      bankruptcy: 0,
      cosignerOnOtherLoan: 0,
      cosignerForWhom: '',
      cosignerToWhom: '',
      otherCredit: false,
      otherCreditName: '',
      partyInLawsuit: 0,
      pastForeclosure: 0,
      pastRepossession: 0,
      judgement: 0,
      incomeDecline: 0,
      obligations: 0,
      pastDueBills: 0,
      textMessageConsent: 0,
    },
  },
  employment: {
    current: {
      employmentStatus: 0,
      employer: '',
      startDate: '',
      addressFields: {
        street: '',
        city: '',
        state: '',
        zip: '',
      },
      occupation: '',
      supervisor: '',
      hoursPerWeek: 0,
      workPhone: '',
    },
    previous: {
      employer: '',
      startDate: '',
      endDate: '',
      address: '',
      hoursPerWeek: 0,
      occupation: '',
    },
  },
  income: {
    grossMonthly: '',
    grossPay: 0,
    grossPayDuration: 'monthly',
    additionalIncome: [],
  },
};

export interface PersonName {
  BirthDate: string;
  City: string;
  CurrGrossMonthPay: number;
  Email: string;
  EmployerName: string;
  ExtraAddress: string;
  First: string;
  HomePhone: string;
  IdentIdNumber: [
    {
      EntryId: number;
      IdentIdNumber: string;
    },
  ];
  IdentIdType: [
    {
      EntryId: number;
      IdentIdType: number;
    },
  ];
  Last: string;
  Locator: number;
  Middle: string;
  MobilePhone: string;
  Ssn: string;
  State: string;
  Street: string;
  Suffix: string;
  Type: number;
  UsPersonFlag: number;
  ZipCode: string;
}

export const defaultPersonName: PersonName = {
  BirthDate: '',
  City: '',
  CurrGrossMonthPay: 0,
  Email: '',
  EmployerName: '',
  ExtraAddress: '',
  First: '',
  HomePhone: '',
  IdentIdNumber: [
    {
      EntryId: 0,
      IdentIdNumber: '',
    },
  ],
  IdentIdType: [
    {
      EntryId: 0,
      IdentIdType: 0,
    },
  ],
  Last: '',
  Locator: 0,
  MobilePhone: '',
  Ssn: '',
  State: '',
  Street: '',
  Type: 0,
  UsPersonFlag: 0,
  ZipCode: '',
  Middle: '',
  Suffix: '',
};

export interface Person {
  index: number;
  form: PersonForm;
  wasFetched: boolean;
  name: PersonName;
  isBorrower: boolean;
  //have to have this separated out for data persistence
  employerAddress: {
    current: {
      street: string;
      city: string;
      state: string;
      zip: string;
    };
    previous: {
      street: string;
      city: string;
      state: string;
      zip: string;
    };
  };
  autoCompletedAddress: string; //have to have this separated out for data persistence
  autoCompletedEmployerAddress: string; // for current employer
  autoCompletedPrevEmployerAddress: string; // for previous employer
}

export const defaultEmployerAddress = {
  current: {
    street: '',
    city: '',
    state: '',
    zip: '',
  },
  previous: {
    street: '',
    city: '',
    state: '',
    zip: '',
  },
};

export const defaultPerson: Person = {
  index: 0,
  form: defaultPersonForm,
  wasFetched: false,
  name: defaultPersonName,
  isBorrower: true,
  employerAddress: defaultEmployerAddress,
  autoCompletedAddress: '',
  autoCompletedEmployerAddress: '',
  autoCompletedPrevEmployerAddress: '',
};
